import type {
    TrendClient,
    TrendDataType,
    TrendTarget,
    TrendWindow,
} from '@/api/dashboardTrends';

/*
 * Pure helpers behind the dashboard client-trends gadget. The server sends
 * per-target window averages; everything judgemental — is this trend real,
 * is it good news — happens here so it's unit-testable without a network.
 */

export const TREND_WINDOWS: ReadonlyArray<{ value: TrendWindow; label: string }> = [
    { value: 7, label: '1 week' },
    { value: 14, label: '2 weeks' },
    { value: 30, label: '1 month' },
];

export type TrendStatus = 'improving' | 'declining' | 'flat' | 'baseline' | 'insufficient';

/*
 * A trend needs one value on each side to compute a delta — so we show a
 * direction as soon as BOTH windows have ≥1 point. ABA data density is low
 * (many targets get one session a week), and a slightly noisy arrow that
 * prompts a look beats a dead "No data": clients seen once a week, or twice
 * with a gap, would otherwise never surface a trend. When a window backing
 * the trend has only a single point, we still show the arrow but flag it as
 * `limited` so the UI can warn that it rests on thin data.
 */
export const MIN_POINTS_FOR_TREND = 1;
/* At or below this many points in a window, a trend is flagged `limited`. */
export const LIMITED_POINTS_PER_WINDOW = 1;

/** Data types whose value_numeric is already a 0–100 percentage. */
const PERCENT_TYPES: ReadonlySet<TrendDataType> = new Set(['trial', 'percent', 'task_analysis']);

/*
 * "Flat" band — deltas smaller than this are session-to-session noise, not
 * movement. Percent-family targets use an absolute band (2 percentage
 * points); frequency/duration are open-ended scales so the band is relative
 * to the previous average (5%, floored at 1 unit so near-zero baselines
 * don't flag every wiggle as a trend).
 */
function flatBand(dataType: TrendDataType, previousAvg: number): number {
    if (PERCENT_TYPES.has(dataType)) return 2;
    return Math.max(1, Math.abs(previousAvg) * 0.05);
}

export interface TrendVerdict {
    status: TrendStatus;
    /** current avg − previous avg; null unless status is a trend direction. */
    delta: number | null;
    /** Trend rests on a window with too few points — show a caution marker. */
    limited: boolean;
}

export function classifyTrend(target: TrendTarget): TrendVerdict {
    const { current, previous } = target;

    // No recent sessions → nothing to report. This is the only "no data"
    // state; a stale prior window alone never blanks a target that has
    // current data.
    if (current.avg === null || current.n < MIN_POINTS_FOR_TREND) {
        return { status: 'insufficient', delta: null, limited: false };
    }

    // Current data but no prior window to compare against — show the level,
    // not a direction. Flag limited when the level itself rests on one point.
    if (previous.avg === null || previous.n < MIN_POINTS_FOR_TREND) {
        return { status: 'baseline', delta: null, limited: current.n <= LIMITED_POINTS_PER_WINDOW };
    }

    const limited =
        current.n <= LIMITED_POINTS_PER_WINDOW || previous.n <= LIMITED_POINTS_PER_WINDOW;
    const delta = current.avg - previous.avg;
    if (Math.abs(delta) < flatBand(target.dataType, previous.avg)) {
        return { status: 'flat', delta, limited };
    }

    // Direction of "better" comes from the mastery criterion: a falling
    // frequency on a behavior-reduction target (at_or_below) is improvement,
    // the same fall on a skill-acquisition target is decline.
    const movedUp = delta > 0;
    const upIsGood = target.masteryDirection === 'at_or_above';
    return { status: movedUp === upIsGood ? 'improving' : 'declining', delta, limited };
}

const round1 = (v: number) => Math.round(v * 10) / 10;

/** Format an average in the target's native unit ('85%', '42s', '5.5'). */
export function formatValue(dataType: TrendDataType, value: number): string {
    if (PERCENT_TYPES.has(dataType)) return `${round1(value)}%`;
    if (dataType === 'duration') return `${round1(value)}s`;
    return `${round1(value)}`;
}

/** Format a delta with an explicit sign ('+4%', '−12s', '+3'). */
export function formatDelta(dataType: TrendDataType, delta: number): string {
    const rounded = round1(delta);
    // U+2212 minus sign reads better than a hyphen next to numerals.
    const sign = rounded < 0 ? '−' : '+';
    return `${sign}${formatValue(dataType, Math.abs(rounded))}`;
}

export interface ClientTrendSummary {
    improving: number;
    flat: number;
    declining: number;
    baseline: number;
    insufficient: number;
}

/** Roll a client's targets up into per-status counts for the collapsed row. */
export function summarizeClient(client: TrendClient): ClientTrendSummary {
    const summary: ClientTrendSummary = {
        improving: 0, flat: 0, declining: 0, baseline: 0, insufficient: 0,
    };
    for (const program of client.programs) {
        for (const target of program.targets) {
            summary[classifyTrend(target).status] += 1;
        }
    }
    return summary;
}
